import React, { useState, useCallback, useRef, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { COLORS } from "../../assets/theme";
import SearchIcon from "@mui/icons-material/Search";
import CustomTextField from "../CustomTextField";
import CustomButton from "../CustomButton";
import CustomSelect from "../CustomSelect";
import DateRangePicker from "../DateRangePicker";
import CustomLabel from "../CustomLabel";

interface ISearchHeaderProps {
  search: string;
  setSearch: (search: string) => void;
  buttonConfig?: {
    label?: string;
    onClick: () => void;
    minHeight?: number;
    minWidth?: number | string;
    [key: string]: any;
  }[];
  selectConfig?: {
    options: { label: string; value: string | number }[];
    value: string | number | (string | number)[];
    onChange: (v: any) => void;
    placeholder?: string;
    size?: "small" | "medium" | "large";
    clearable?: boolean;
    searchable?: boolean;
    multiSelect?: boolean;
    label?: string;
    labelBgColor?: string;
    hidden?: boolean;
    sx?: any;
  } | null;
  selectConfig2?: {
    options: { label: string; value: string | number }[];
    value: string | number | (string | number)[];
    onChange: (v: any) => void;
    placeholder?: string;
    size?: "small" | "medium" | "large";
    clearable?: boolean;
    searchable?: boolean;
    multiSelect?: boolean;
    label?: string;
    labelBgColor?: string;
    hidden?: boolean;
    sx?: any;
  } | null;
  dateRangeConfig?: {
    value?: { start?: string; end?: string } | [string?, string?];
    onChange: (v: any) => void;
    size?: "small" | "medium";
    fullWidth?: boolean;
  } | null;
}

function SearchHeaderContainer({
  search,
  setSearch,
  buttonConfig = [],
  selectConfig = null,
  selectConfig2 = null,
  dateRangeConfig = null,
}: ISearchHeaderProps) {
  const [localSearch, setLocalSearch] = useState(search);
  const debounceRef = useRef<number | undefined>(undefined);

  const handleDebouncedChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setLocalSearch(value);
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
      debounceRef.current = window.setTimeout(() => setSearch(value), 300);
    },
    [setSearch]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <Box
      mb={1}
      display="flex"
      alignItems="center"
      flexWrap="wrap"
      sx={{
        position: "relative",
        gap: 1,
        width: "100%",
        px: { xs: 1, sm: 0 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          width: { xs: "100%", sm: "auto" },
          mr: 1,
          mt: 2.6,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <CustomTextField
          size="small"
          startIcon={<SearchIcon />}
          placeholder="Search"
          bgColor={COLORS.WhiteBackground}
          value={localSearch}
          onChange={handleDebouncedChange}
          sx={{
            width: { xs: "100%", sm: 200, md: 220, lg: 240 },
            maxWidth: 240,
          }}
        />

        {(selectConfig || selectConfig2) && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              mt: { xs: 1, sm: 0 },
              width: { xs: "100%", sm: "auto" },
              flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
          >
            {selectConfig && (
              <Box
                sx={{
                  width: { xs: "100%", sm: 160, md: 180 },
                  maxWidth: 180,
                  flexShrink: 0,
                  position: "relative",
                }}
              >
                {selectConfig.label && (
                  <CustomLabel
                    text={selectConfig.label}
                    sx={{
                      position: "absolute",
                      top: -22,
                      left: 0,
                      backgroundColor:
                        selectConfig.labelBgColor ?? COLORS.MainBackground,
                      px: 0,
                      fontSize: "0.95rem",
                      pointerEvents: "none",
                    }}
                  />
                )}
                <CustomSelect
                  options={selectConfig.options}
                  value={selectConfig.value}
                  onChange={selectConfig.onChange}
                  placeholder={selectConfig.placeholder}
                  size={selectConfig.size}
                  hidden={selectConfig.hidden}
                  label={selectConfig.label}
                  searchable={selectConfig.searchable}
                  multiSelect={selectConfig.multiSelect}
                  clearable={selectConfig.clearable}
                  sx={{ ...(selectConfig.sx || {}), width: "100%" }}
                />
              </Box>
            )}

            {selectConfig2 && (
              <Box
                sx={{
                  width: { xs: "100%", sm: 160, md: 180 },
                  maxWidth: 180,
                  flexShrink: 0,
                  position: "relative",
                  backgroundColor: "white",
                }}
              >
                {selectConfig2.label && (
                  <CustomLabel
                    text={selectConfig2.label}
                    sx={{
                      position: "absolute",
                      top: -22,
                      left: 0,
                      backgroundColor:
                        selectConfig2.labelBgColor ?? COLORS.MainBackground,
                      px: 0,
                      fontSize: "0.95rem",
                      pointerEvents: "none",
                    }}
                  />
                )}
                <CustomSelect
                  options={selectConfig2.options}
                  value={selectConfig2.value}
                  onChange={selectConfig2.onChange}
                  placeholder={selectConfig2.placeholder}
                  size={selectConfig2.size}
                  hidden={selectConfig2.hidden}
                  label={selectConfig2.label}
                  searchable={selectConfig2.searchable}
                  multiSelect={selectConfig2.multiSelect}
                  clearable={selectConfig2.clearable}
                  sx={{ ...(selectConfig2.sx || {}), width: "100%" }}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>

      {dateRangeConfig && (
        <Box
          sx={{
            width: { xs: "100%", sm: 220, md: 260 },
            ml: { xs: 0, sm: 0 },
            alignSelf: { xs: "center", sm: "flex-start" },
            mt: { xs: 1, sm: 0 },
            maxWidth: { xs: "100%", sm: 320 },
          }}
        >
          <DateRangePicker
            value={dateRangeConfig.value}
            onChange={dateRangeConfig.onChange}
            size={dateRangeConfig.size}
          />
        </Box>
      )}

      {buttonConfig.length > 0 && (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            ml: { xs: 0, sm: "auto" },
            alignItems: "center",
            mt: 2.5,
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "center", sm: "flex-start" },
          }}
        >
          {buttonConfig.map((button, index) => {
            const mergedSx: any = {
              width: { xs: "100%", sm: "auto" },
              ...(button.sx || {}),
            };
            if (button.minWidth !== undefined) {
              mergedSx.minWidth = button.minWidth;
            }
            if (button.maxWidth !== undefined) {
              mergedSx.maxWidth = button.maxWidth;
            }

            return (
              <CustomButton
                key={index}
                {...button}
                minHeight={button.minHeight}
                minWidth={
                  typeof button.minWidth === "number"
                    ? button.minWidth
                    : undefined
                }
                disabled={button.disabled}
                sx={mergedSx}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
}

export default SearchHeaderContainer;
